import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "sat_satbot_session";

// Hidden on these routes
const HIDDEN_ROUTES = ["/admin", "/auth", "/checkout", "/los/scan", "/herzbewegung"];

const STARTERS = [
  "Welches Programm passt zu mir?",
  "Ich möchte einen Termin",
  "Was kostet ein Retreat?",
  "Erzähl mir vom SAT Master",
];

export default function SatBotWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const sid = window.localStorage.getItem(STORAGE_KEY);
    if (sid) setSessionId(sid);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, busy]);

  const isHidden = HIDDEN_ROUTES.some((r) => pathname.startsWith(r));
  if (isHidden) return null;

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/satbot/chat`, {
        session_id: sessionId,
        message: trimmed,
      });
      const { session_id, reply, intent, booking_id } = r.data;
      if (session_id && session_id !== sessionId) {
        setSessionId(session_id);
        window.localStorage.setItem(STORAGE_KEY, session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: reply, intent, booking_id }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Etwas ist schiefgelaufen. Versuch es bitte gleich nochmal — oder ruf uns direkt an.", error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
    window.localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="SATbot öffnen"
          className="fixed bottom-24 right-6 z-[120] inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold shadow-2xl transition-transform hover:scale-105"
          style={{
            background: "linear-gradient(135deg,#D4AF37 0%,#e3c252 100%)",
            color: "#081328",
            boxShadow: "0 12px 32px rgba(212,175,55,0.35)",
          }}
          data-testid="satbot-toggle"
        >
          <MessageCircle size={18} />
          <span className="text-sm">SATbot</span>
          <span className="ml-1 w-2 h-2 rounded-full bg-[#48d597] animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[120] w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-7rem)] rounded-2xl overflow-hidden flex flex-col"
          style={{
            background: "linear-gradient(180deg,#0E1A30 0%,#081328 100%)",
            border: "1px solid rgba(212,175,55,0.3)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
          }}
          data-testid="satbot-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center"
                   style={{ background: "radial-gradient(circle,#D4AF37 0%,#4B2E83 100%)" }}>
                <Sparkles size={16} className="text-white" />
              </div>
              <div>
                <div className="serif text-base text-white">SATbot</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">Bewusster Concierge · online</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button onClick={reset} className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70" data-testid="satbot-reset">Neu</button>
              )}
              <button onClick={() => setOpen(false)} aria-label="Schließen" className="text-white/55 hover:text-white p-1" data-testid="satbot-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="satbot-messages">
            {messages.length === 0 && (
              <div className="space-y-4">
                <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4 text-sm leading-relaxed text-white/80">
                  Willkommen.<br/>
                  Ich bin <b className="text-[#D4AF37]">SATbot</b> — dein bewusster Assistent der Schweizer Akademie für Transformation. Frag mich nach Programmen, Preisen oder buch direkt einen Termin bei Nenad.
                </div>
                <div className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-white/40">Beispielfragen</div>
                  {STARTERS.map((s) => (
                    <button key={s} onClick={() => send(s)}
                      className="block w-full text-left px-3 py-2 text-sm bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 rounded-lg text-white/75 hover:text-white transition"
                      data-testid={`starter-${s.slice(0,12).replace(/\s/g,'-')}`}>
                      → {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#D4AF37] text-[#081328] rounded-br-sm"
                    : "bg-white/[0.06] text-white/90 border border-white/10 rounded-bl-sm"
                }`}>
                  {m.content}
                  {m.intent === "appointment" && m.booking_id && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#48d597] border border-[#48d597]/40 px-2 py-0.5 rounded">
                      ✓ Termin vorgemerkt
                    </div>
                  )}
                  {m.intent === "lead" && m.booking_id && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#a48cff] border border-[#a48cff]/40 px-2 py-0.5 rounded">
                      ✓ Anfrage erfasst
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/[0.06] border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3 text-white/60">
                  <Loader2 size={14} className="animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-white/10 p-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Schreib eine Nachricht…"
              rows={1}
              className="flex-1 bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none resize-none max-h-32"
              data-testid="satbot-input"
            />
            <button type="submit" disabled={busy || !input.trim()}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D4AF37] text-[#081328] disabled:opacity-40 hover:bg-[#e3c252] transition"
              data-testid="satbot-send">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
